import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Info, Moon, Sun, X, FileText, Image as ImageIcon } from "lucide-react";
import { jsPDF } from "jspdf";
import heic2any from "heic2any";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd, DesktopOnlyAd } from "../components/AdPlacement";

const PAGE_SIZES = {
  "a4": { name: "A4", width: 210, height: 297 },
  "letter": { name: "Letter", width: 215.9, height: 279.4 },
  "fit": { name: "Fit to Image", width: 0, height: 0 },
};

// Template component for single-format image to PDF conversion
function SingleImageToPdf({ 
  format, 
  formatName, 
  acceptTypes, 
  color = "red",
  description,
  needsConversion = false
}) {
  const [images, setImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pageSize, setPageSize] = useState("a4");
  const [orientation, setOrientation] = useState("portrait");
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  const colorClasses = {
    red: { bg: "from-red-500 to-red-600", text: "text-red-500", hover: "bg-red-600 hover:bg-red-700" },
    blue: { bg: "from-blue-500 to-blue-600", text: "text-blue-500", hover: "bg-blue-600 hover:bg-blue-700" },
    green: { bg: "from-green-500 to-green-600", text: "text-green-500", hover: "bg-green-600 hover:bg-green-700" },
    purple: { bg: "from-purple-500 to-purple-600", text: "text-purple-500", hover: "bg-purple-600 hover:bg-purple-700" },
    orange: { bg: "from-orange-500 to-orange-600", text: "text-orange-500", hover: "bg-orange-600 hover:bg-orange-700" },
    cyan: { bg: "from-cyan-500 to-cyan-600", text: "text-cyan-500", hover: "bg-cyan-600 hover:bg-cyan-700" },
    pink: { bg: "from-pink-500 to-pink-600", text: "text-pink-500", hover: "bg-pink-600 hover:bg-pink-700" },
    amber: { bg: "from-amber-500 to-amber-600", text: "text-amber-500", hover: "bg-amber-600 hover:bg-amber-700" },
  };
  const colors = colorClasses[color] || colorClasses.red;

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = `${formatName} to PDF - Convert ${formatName} to PDF - KayTool`;
  }, [formatName]);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    await processFiles(files);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await processFiles(files);
    }
  };

  const processFiles = async (files) => {
    setIsProcessing(true);
    setProgress(0);

    const newImages = [];

    for (let i = 0; i < files.length; i++) {
      let file = files[i];
      
      // Convert HEIC/HEIF if needed
      if (needsConversion && (file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif'))) {
        try {
          const convertedBlob = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.95 });
          file = new File([convertedBlob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
        } catch (error) {
          toast.error(`Failed to convert ${file.name}`);
          continue;
        }
      }

      const preview = URL.createObjectURL(file);
      const dimensions = await getImageDimensions(file);

      newImages.push({
        id: Date.now() + i,
        name: file.name,
        file: file,
        preview: preview,
        width: dimensions.width,
        height: dimensions.height,
        size: file.size,
      });

      setProgress(Math.round(((i + 1) / files.length) * 100));
    }

    setImages(prev => [...prev, ...newImages]);
    setIsProcessing(false);
    if (newImages.length > 0) {
      toast.success(`Added ${newImages.length} ${formatName} file${newImages.length > 1 ? 's' : ''}`);
    }
  };

  const getImageDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => resolve({ width: 100, height: 100 });
      img.src = URL.createObjectURL(file);
    });
  };

  const loadImageAsDataUrl = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const convertToPdf = async () => {
    if (images.length === 0) {
      toast.error(`Please add ${formatName} files first`);
      return;
    }

    setIsProcessing(true);
    setProgress(0);

    try {
      for (let imgIndex = 0; imgIndex < images.length; imgIndex++) {
        const image = images[imgIndex];
        const dataUrl = await loadImageAsDataUrl(image.file);
        
        let pageWidth, pageHeight;
        
        if (pageSize === "fit") {
          // Fit to image dimensions (convert pixels to mm at 96 DPI)
          pageWidth = (image.width / 96) * 25.4;
          pageHeight = (image.height / 96) * 25.4;
        } else {
          const page = PAGE_SIZES[pageSize];
          const isLandscape = orientation === "landscape";
          pageWidth = isLandscape ? page.height : page.width;
          pageHeight = isLandscape ? page.width : page.height;
        }
        
        const pdf = new jsPDF({
          orientation: pageSize === "fit" ? (image.width > image.height ? 'landscape' : 'portrait') : orientation,
          unit: 'mm',
          format: [pageWidth, pageHeight]
        });

        if (pageSize === "fit") {
          pdf.addImage(dataUrl, 'JPEG', 0, 0, pageWidth, pageHeight);
        } else {
          const margin = 10;
          const imgRatio = image.width / image.height;
          const availableWidth = pageWidth - (margin * 2);
          const availableHeight = pageHeight - (margin * 2);
          const availableRatio = availableWidth / availableHeight;
          
          let imgWidth, imgHeight;
          if (imgRatio > availableRatio) {
            imgWidth = availableWidth;
            imgHeight = availableWidth / imgRatio;
          } else {
            imgHeight = availableHeight;
            imgWidth = availableHeight * imgRatio;
          }
          
          const x = (pageWidth - imgWidth) / 2;
          const y = (pageHeight - imgHeight) / 2;
          
          pdf.addImage(dataUrl, 'JPEG', x, y, imgWidth, imgHeight);
        }

        const pdfName = image.name.replace(/\.[^/.]+$/, '.pdf');
        pdf.save(pdfName);
        
        setProgress(Math.round(((imgIndex + 1) / images.length) * 100));
      }

      toast.success(`Converted ${images.length} file${images.length > 1 ? 's' : ''} to PDF!`);
    } catch (error) {
      console.error("PDF creation error:", error);
      toast.error("Failed to create PDF");
    }

    setIsProcessing(false);
  };

  const removeImage = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  const clearAll = () => {
    setImages([]);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-slate-50 to-slate-100'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colors.bg} flex items-center justify-center`}>
              <FileText className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Info className="w-3 h-3" /> 100% Local
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className={`inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r ${colors.bg} text-white rounded-full mb-4`}>
            <ImageIcon className="w-5 h-5" />
            <span className="font-medium">{formatName} to PDF</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Convert {formatName} to PDF
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            {description || `Convert your ${formatName} images to PDF format. Fast, free, and secure.`}
          </p>
        </div>

        {/* Settings */}
        <Card className="mb-6 dark:bg-slate-800">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">Page Size</Label>
                <Select value={pageSize} onValueChange={setPageSize}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PAGE_SIZES).map(([key, val]) => (
                      <SelectItem key={key} value={key}>
                        {val.name} {val.width > 0 ? `(${val.width}×${val.height}mm)` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {pageSize !== "fit" && (
                <div>
                  <Label className="text-sm font-medium mb-2 block">Orientation</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={orientation === "portrait" ? "default" : "outline"}
                      onClick={() => setOrientation("portrait")}
                      className={`flex-1 ${orientation === "portrait" ? colors.hover : ""}`}
                    >
                      Portrait
                    </Button>
                    <Button
                      variant={orientation === "landscape" ? "default" : "outline"}
                      onClick={() => setOrientation("landscape")}
                      className={`flex-1 ${orientation === "landscape" ? colors.hover : ""}`}
                    >
                      Landscape
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Upload Area */}
        <Card 
          className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 ${
            isDragging ? `border-${color}-500 bg-${color}-50 dark:bg-${color}-900/20` : 'border-slate-300 dark:border-slate-600'
          }`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <CardContent className="p-12 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptTypes}
              multiple
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
            <div 
              className="cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-zone"
            >
              <Upload className={`w-16 h-16 mx-auto mb-4 ${colors.text}`} />
              <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                Drop {formatName} files or click to upload
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-4">
                Each file will be converted to a separate PDF
              </p>
              <Badge variant="secondary">Multiple files supported</Badge>
            </div>
          </CardContent>
        </Card>

        {isProcessing && (
          <Card className="mt-6 dark:bg-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Progress value={progress} className="h-3" />
                </div>
                <span className="text-sm font-medium">{progress}%</span>
              </div>
            </CardContent>
          </Card>
        )}

        {images.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                Files ({images.length})
              </h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={clearAll} className="text-red-600">
                  <X className="w-4 h-4 mr-1" /> Clear All
                </Button>
                <Button 
                  onClick={convertToPdf} 
                  disabled={isProcessing}
                  data-testid="convert-button"
                  className={`bg-gradient-to-r ${colors.bg} hover:opacity-90`}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Convert to PDF
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {images.map((image) => (
                <Card key={image.id} className="dark:bg-slate-800 overflow-hidden">
                  <div className="aspect-square relative bg-slate-100 dark:bg-slate-700">
                    <img src={image.preview} alt={image.name} className="w-full h-full object-contain" />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 w-6 h-6"
                      onClick={() => removeImage(image.id)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                  <CardContent className="p-2">
                    <p className="text-xs text-slate-600 dark:text-slate-400 truncate">{image.name}</p>
                    <p className="text-xs text-slate-400">{formatSize(image.size)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

// Export individual converters
export const JpgToPdf = () => (
  <SingleImageToPdf 
    format="jpg" 
    formatName="JPG" 
    acceptTypes=".jpg,.jpeg,image/jpeg"
    color="orange"
    description="Convert JPG/JPEG images to PDF format. Perfect for documents, photos, and archives."
  />
);

export const PngToPdf = () => (
  <SingleImageToPdf 
    format="png" 
    formatName="PNG" 
    acceptTypes=".png,image/png"
    color="blue"
    description="Convert PNG images to PDF format. Preserves transparency as white background."
  />
);

export const WebpToPdf = () => (
  <SingleImageToPdf 
    format="webp" 
    formatName="WebP" 
    acceptTypes=".webp,image/webp"
    color="green"
    description="Convert WebP images to PDF format. Modern format to universal PDF."
  />
);

export const HeicToPdf = () => (
  <SingleImageToPdf 
    format="heic" 
    formatName="HEIC" 
    acceptTypes=".heic,.heif"
    color="purple"
    description="Convert iPhone HEIC/HEIF photos to PDF format. Perfect for sharing Apple photos."
    needsConversion={true}
  />
);

export const SvgToPdf = () => (
  <SingleImageToPdf 
    format="svg" 
    formatName="SVG" 
    acceptTypes=".svg,image/svg+xml"
    color="cyan"
    description="Convert SVG vector graphics to PDF format. Great for logos and illustrations."
  />
);

export const TiffToPdf = () => (
  <SingleImageToPdf 
    format="tiff" 
    formatName="TIFF" 
    acceptTypes=".tiff,.tif,image/tiff"
    color="amber"
    description="Convert TIFF images to PDF format. Professional format for high-quality documents."
  />
);

export default SingleImageToPdf;
